// Anti-portfolio — passed deals that went on to work out. Server-backed:
// rows live on the firm (added from passed deals in Detail, or manually),
// and outcomes are edited here as they become known.

import { useMemo, useState } from "react";
import { addAnti, deleteAnti, patchAnti } from "../api";
import { FEATURES } from "../features";
import { findMember, useAppData } from "../store";
import type { AntiRow } from "../types";
import { Avatar, Btn, Chip, TextInput } from "../components/Primitives";
import { Logo } from "../components/Logo";

function OutcomeEditor({ row, onDone }: { row: AntiRow; onDone: () => void }) {
  const { refresh } = useAppData();
  const [then, setThen] = useState(row.then === "—" ? "" : row.then);
  const [mult, setMult] = useState(row.mult === "—" ? "" : row.mult);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await patchAnti(row.id!, { then: then.trim() || "—", mult: mult.trim() || "—" });
      await refresh();
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
      <TextInput placeholder="What happened (e.g. raised at $400M)" value={then}
                 onChange={setThen} style={{ flex: 2 }} />
      <TextInput placeholder="Multiple (e.g. 20×)" value={mult}
                 onChange={setMult} style={{ width: 110 }} />
      <Btn size="sm" kind="primary" onClick={save} disabled={busy}>
        {busy ? "Saving…" : "Save"}
      </Btn>
      <Btn size="sm" kind="ghost" onClick={onDone}>Cancel</Btn>
    </div>
  );
}

export function AntiScreen() {
  const { data, refresh } = useAppData();
  const [partner, setPartner] = useState("all");
  const [reason,  setReason]  = useState("all");
  const [editing, setEditing] = useState<string | null>(null);
  const [adding,  setAdding]  = useState(false);
  const [newCo, setNewCo] = useState("");
  const [newReason, setNewReason] = useState("");

  const all: AntiRow[] = useMemo(() => data?.anti ?? [], [data?.anti]);

  const partners = useMemo(
    () => Array.from(new Set(all.map((r) => r.who).filter(Boolean))),
    [all],
  );
  const reasons = useMemo(
    () => Array.from(new Set(all.map((r) => r.reasonThen).filter(
      (x) => Boolean(x) && x !== "—"))),
    [all],
  );

  const rows = all.filter(
    (r) =>
      (partner === "all" || r.who === partner) &&
      (reason  === "all" || r.reasonThen === reason),
  );

  const submitAdd = async () => {
    if (!newCo.trim()) return;
    await addAnti({ co: newCo.trim(), reasonThen: newReason.trim() || "—" });
    await refresh();
    setNewCo(""); setNewReason(""); setAdding(false);
  };

  const remove = async (id: string) => {
    await deleteAnti(id);
    await refresh();
  };

  return (
    <div className="page">
      <div className="topbar">
        <h1 style={{ fontFamily: "var(--serif)", fontWeight: 500, letterSpacing: "-0.01em" }}>
          Anti‑portfolio
        </h1>
        <span className="crumb">· what we passed on that worked out</span>
        <span className="grow" />
        {FEATURES.antiAddCompany && (
          <Btn size="sm" ico="plus" kind="primary" onClick={() => setAdding((x) => !x)}>
            Add company
          </Btn>
        )}
      </div>

      <div className="filter-bar">
        <Chip on={partner === "all"} onClick={() => setPartner("all")}>All partners</Chip>
        {partners.map((p) => (
          <Chip key={p} on={partner === p} onClick={() => setPartner(p)}>
            <Avatar id={p} size="xs" />
            <span style={{ marginLeft: 4 }}>
              {findMember(data?.config.team, p)?.name ?? p}
            </span>
          </Chip>
        ))}
        <div style={{ width: 1, height: 18, background: "var(--border)", margin: "0 4px" }} />
        <Chip on={reason === "all"} onClick={() => setReason("all")}>All reasons</Chip>
        {reasons.map((r) => (
          <Chip key={r} on={reason === r} onClick={() => setReason(r)}>{r}</Chip>
        ))}
      </div>

      <div className="scroll">
        <div style={{ maxWidth: 920, padding: "26px 28px 80px" }}>
          {adding && FEATURES.antiAddCompany && (
            <div style={{
              display: "flex", gap: 10, marginBottom: 22, padding: 14,
              background: "var(--surface)", border: "1px solid var(--border)",
              borderRadius: 8,
            }}>
              <TextInput placeholder="Company" value={newCo}
                         onChange={setNewCo} style={{ flex: 1 }} />
              <TextInput placeholder="Reason you passed" value={newReason}
                         onChange={setNewReason} style={{ flex: 1 }} />
              <Btn kind="primary" size="sm" onClick={submitAdd}>Add</Btn>
            </div>
          )}
          <p style={{
            color: "var(--ink-2)", fontSize: 13.5, lineHeight: 1.6,
            margin: "0 0 28px", maxWidth: 560,
          }}>
            {rows.length} of {all.length} entries — passed deals that raised at a
            higher valuation, were acquired, or went public. Add misses from a
            passed deal's page, and record the outcome here when it lands.
          </p>
          {rows.length === 0 ? (
            <div className="empty">
              <h3>Nothing here yet</h3>
              <p>Pass on something great first — then own it here.</p>
            </div>
          ) : rows.map((r) => (
            <div key={r.id ?? r.co} style={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr 1.6fr 110px",
              gap: 22, padding: "22px 4px",
              borderTop: "1px solid var(--border)",
              alignItems: "baseline",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <Logo domain={r.domain} co={r.co} size={36} radius={8} />
                <div>
                  <div style={{
                    fontFamily: "var(--serif)", fontSize: 19, fontWeight: 500,
                    letterSpacing: "-0.01em",
                  }}>{r.co}</div>
                  <div style={{
                    color: "var(--ink-3)", fontSize: 11.5, marginTop: 4,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    Passed by <Avatar id={r.who} size="xs" />
                    {findMember(data?.config.team, r.who)?.name ?? r.who}
                    {r.score != null && <>{" · score "}{r.score}</>}
                    {" · "}{r.date}
                  </div>
                </div>
              </div>
              <div>
                <div className="eyebrow">Reason at the time</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{r.reasonThen}</div>
              </div>
              <div>
                <div className="eyebrow">What happened</div>
                {editing === r.id ? (
                  <OutcomeEditor row={r} onDone={() => setEditing(null)} />
                ) : (
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {r.then}
                    {r.id && (
                      <span
                        style={{
                          marginLeft: 8, color: "var(--ink-3)", cursor: "pointer",
                          fontSize: 11.5, textDecoration: "underline",
                          textUnderlineOffset: 2,
                        }}
                        onClick={() => setEditing(r.id!)}
                      >edit</span>
                    )}
                    {r.id && (
                      <span
                        style={{
                          marginLeft: 8, color: "var(--ink-4)", cursor: "pointer",
                          fontSize: 11.5,
                        }}
                        onClick={() => void remove(r.id!)}
                      >remove</span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontFamily: "var(--serif)", fontSize: 26, fontWeight: 500,
                  letterSpacing: "-0.02em",
                }}>{r.mult}</div>
                <div style={{
                  color: "var(--ink-3)", fontSize: 10.5,
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  fontWeight: 500, marginTop: 2,
                }}>vs pass valuation</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
